<script>
import { UtilsMixin } from "@/mixins/utils.mixin";
import GoogleMapsMixin from "@/mixins/google-maps.mixin";
import OmniOmniTextarea from "@/components/omni/OmniTextarea.vue";
import OmniDialog from "@/components/omni/OmniDialog.vue";
import FacilityEditForm from "@/views/companies/facilities/edit/components/EditForm.vue";
import { mapActions } from "pinia";
import { useSnackbarStore } from "@/store/snackbar.store";
import debounce from "lodash.debounce";
import { FacilityType } from "@/constants/facility.js";
import { useOrder } from "@/composables/index.js";
import PermissionTooltip from "@/components/tooltips/PermissionTooltip.vue";
import { DispatcherPermissionValue } from "@/api/dto/dispatcher.dto.ts";

const { generateId } = useOrder();

export default {
  name: "FacilitySelect",
  components: { PermissionTooltip, FacilityEditForm, OmniDialog, OmniTextarea: OmniOmniTextarea },
  mixins: [UtilsMixin, GoogleMapsMixin],
  props: {
    name: String,
    label: String,
    modelValue: Number,
    readonly: {
      type: Boolean,
      default: false,
    },
    rules: Array,
    required: {
      type: Boolean,
      default: false,
    },
    editable: {
      type: Boolean,
      default: false,
    },
    hideDetails: {
      type: Boolean,
      default: false,
    },
    coordinatesTooltip: String,
    headerFacilities: {
      type: Array,
      default: () => [],
    },
    headerFacilitiesDividerText: {
      type: String,
      default: "",
    },
  },
  emits: ["update:modelValue", "change", "update"],
  data() {
    return {
      model: null,
      addDialog: false,
      isLoading: false,
      search: null,
      searchedFacilities: [],
      facility: null,
      location: null,
      menu: false,
      debouncedFetchFacility: () => {},
    };
  },
  computed: {
    DispatcherPermissionValue() {
      return DispatcherPermissionValue;
    },
    FacilityType() {
      return FacilityType;
    },
    items() {
      const uniqueIds = new Set();

      const headerFacilities = !this.search
        ? [
            { type: "divider", text: this.headerFacilitiesDividerText, id: generateId() },
            ...this.headerFacilities,
            { type: "divider", text: "Other:", id: generateId() },
          ]
        : [];

      const result = [...headerFacilities, ...this.searchedFacilities].filter((facility) => {
        if (facility?.type === "divider") {
          return true;
        }

        if (uniqueIds.has(facility?.id)) {
          return false;
        }

        uniqueIds.add(facility?.id);
        return true;
      });

      return result;
    },
  },
  watch: {
    search(val) {
      this.debouncedFetchFacility(val);
    },
    async modelValue() {
      this.model = this.modelValue;
      if (!this.items.some((u) => u.id === this.modelValue)) {
        await this.preload(this.modelValue);
      }
    },
  },
  async mounted() {
    this.model = this.modelValue;
    await this.preload(this.modelValue);

    this.debouncedFetchFacility = debounce(this.fetchData, 300);
  },
  methods: {
    ...mapActions(useSnackbarStore, ["showSnackbarError"]),
    openFacilityDialog() {
      this.addDialog = true;
    },
    closeFacilityDialog() {
      this.addDialog = false;
    },
    addFacility() {
      this.facility = null;
      this.openFacilityDialog();
    },
    changed(value) {
      this.$emit("update:modelValue", value);
      this.$emit("change", value);
      const facility = this.items.find((f) => f.id === value);
      if (facility) {
        this.facility = facility;
        this.$emit("update", facility);
      }
    },
    async fetchData(term = "") {
      const params = { search: term, pageSize: 10 };
      this.isLoading = true;

      try {
        const resp = await this.$api.company.findFacilities(params);
        const searchResult = resp.data.items.map((f) => {
          f.fullAddress = f.addressLine + " " + f.address;
          return f;
        });

        if (!this.facility || this.search) {
          this.searchedFacilities = searchResult;
        } else {
          this.searchedFacilities = this.mergeById([this.facility], searchResult);
        }

        if (this.search && this.searchedFacilities.length) {
          this.menu = true;
        }
      } catch (err) {
        console.error("Error loading facilities", err);
      } finally {
        this.isLoading = false;
      }
    },
    async preload(id) {
      if (!id) return;

      this.isLoading = true;

      try {
        const resp = await this.$api.company.findFacilityById(id);
        this.facility = resp.data;
        this.searchedFacilities = [this.facility];
      } catch (err) {
        console.error("Error loading facility data", err);
      } finally {
        this.isLoading = false;
      }
    },
    async onSaved(facility) {
      this.closeFacilityDialog();

      this.facility = facility;
      this.searchedFacilities.push(facility);

      this.changed(facility?.id);
    },
    onFocusChanged(focus) {
      this.menu = focus;

      if (!focus) {
        this.searchedFacilities = [this.facility];
      } else {
        this.fetchData();
      }
    },
  },
};
</script>

<template>
  <div>
    <div class="d-flex align-center facility-block">
      <v-autocomplete
        v-model="model"
        v-model:search="search"
        :class="{ required: required }"
        :clearable="!readonly"
        :disabled="readonly"
        :items="items"
        :label="label"
        :loading="isLoading"
        :menu="menu"
        :name="name"
        :readonly="readonly"
        :rules="rules"
        autocomplete="off"
        class="mb-0 width-100"
        color="primary"
        density="compact"
        hide-details="auto"
        hide-no-data
        item-title="name"
        item-value="id"
        no-filter
        placeholder="Type first letters for facility name"
        variant="outlined"
        @update:model-value="changed"
        @update:focused="onFocusChanged"
      >
        <template #divider="{ props }">
          <div class="px-4 px-2">
            <span class="font-size-11 text-grey-darken-1">
              {{ props.text }}
            </span>
          </div>
        </template>
        <template #item="{ props, item }">
          <v-list-item :subtitle="item?.raw?.fullAddress || item?.raw?.address" :title="item?.raw?.name" v-bind="props">
            <template #append>
              <div class="d-flex align-center col-gap-4">
                <span v-if="item?.raw?.stopShortInfo" class="font-size-12 text-grey-darken-1">
                  {{ item.raw.stopShortInfo }}
                </span>
                <span class="font-size-12 text-grey-darken-3">
                  {{ item?.raw?.type.charAt(0).toUpperCase() + item?.raw?.type.slice(1) }}
                </span>
              </div>
            </template>
          </v-list-item>
        </template>
        <template #selection="{ item }"> {{ item?.raw?.name }}</template>
      </v-autocomplete>

      <permission-tooltip v-if="editable" :permission="DispatcherPermissionValue.EditOrCreateCompany">
        <template #activator="{ disabled }">
          <v-btn
            :disabled="readonly || disabled"
            class="ml-1"
            color="primary"
            height="40"
            icon="mdi-plus"
            variant="text"
            width="40"
            @click="addFacility"
          />
        </template>

        {{ $t("permission.create-company") }}
      </permission-tooltip>
    </div>
    <div v-if="facility?.id && !hideDetails" class="light rounded mt-3 pa-3 height-full">
      <div class="height-full__top pb-6">
        <div
          :class="{ 'text-grey-lighten-1': readonly, 'text-grey-darken-1': !readonly }"
          class="font-size-12 font-weight-500 mb-2"
        >
          Type: {{ facility.type.charAt(0).toUpperCase() + facility.type.slice(1) }}
        </div>
        <router-link
          :to="{ name: 'facilities-edit', params: { id: facility?.id } }"
          class="text-primary font-weight-500 text-decoration-none d-block mb-2"
          data-qa="card-facility-name"
          target="_blank"
        >
          {{ facility.name }}
        </router-link>
        <div
          v-if="facility.type === FacilityType.point && facility.addressCoordinates"
          :class="{ 'text-grey-lighten-1': readonly, 'text-grey-darken-2': !readonly }"
        >
          {{ facility.addressCoordinates.lat }}, {{ facility.addressCoordinates.lon }}
        </div>
        <div
          v-else
          :class="{ 'text-grey-lighten-1': readonly, 'text-grey-darken-2': !readonly }"
          data-qa="card-facility-address-line"
        >
          {{ facility.addressLine }}
        </div>
        <div
          :class="{ 'text-grey-lighten-1': readonly, 'text-grey-darken-2': !readonly }"
          class="mb-2"
          data-qa="card-facility-address"
        >
          {{ facility.address }}
        </div>
        <div class="d-flex align-center">
          <v-icon :color="readonly ? 'grey-lighten-1' : 'green'" class="mr-2" size="xsmall">
            mdi-map-marker-radius-outline
          </v-icon>
          <a
            :href="getPlaceUrl(facility.addressCoordinates)"
            class="text-green caption text-decoration-underline"
            target="_blank"
          >
            Lat: {{ facility.addressCoordinates.lat }} Lon: {{ facility.addressCoordinates.lon }}
          </a>
          <v-tooltip v-if="coordinatesTooltip" location="top">
            <template #activator="{ props }">
              <v-icon
                :color="readonly ? 'grey-lighten-1' : 'primary'"
                class="ml-2"
                icon="mdi-information-outline"
                v-bind="props"
              ></v-icon>
            </template>
            {{ coordinatesTooltip }}
          </v-tooltip>
        </div>
      </div>
      <div class="height-full__bottom">
        <omni-textarea
          v-model="facility.note"
          :disabled="readonly"
          :readonly="true"
          label="Facility note"
          rows="4"
        ></omni-textarea>
      </div>
    </div>

    <omni-dialog v-model="addDialog" size="large" title="Create facility">
      <facility-edit-form modal @change="onSaved" @close="closeFacilityDialog"></facility-edit-form>
    </omni-dialog>
  </div>
</template>

<style lang="scss" scoped>
.dialog-btn {
  padding: 0 !important;
  margin-left: 12px;
  min-width: auto !important;
  height: auto !important;
}

.modal-close {
  position: absolute;
  top: 15px;
  right: 15px;
  cursor: pointer;
}

.facility-block {
  &:deep(.v-autocomplete__selection) {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    display: inline-block;
  }
}
</style>
